import { useCallback } from 'react';

import {
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  COMPONENT_LAYOUT_GRID,
  COMPONENT_LAYOUT_LIST,
  InquisitiveComponentLayout,
} from 'global/constants';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import StimulusUsageMessage from '../shared/stimulus-message';
import {
  SettingsTitleView,
  SettingsRichtextView,
  SettingsThumbnailView,
} from '../shared/settings-view';
import { HrStyled } from 'approot/shared/styles/decorative.style';
import { spacing24 } from 'approot/shared/styles/spacing.style';
import {
  SettingsGroup,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { EditProps } from './link.constants';
import { CheckableSingle } from 'approot/shared/styles/buttons/checkable-input';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const LinkEditPreviewDrawer = ({
  id,
  type,
  sectionId,
  properties,
  stimulusResourceProperties,
  lessonId,
}: EditProps) => {
  const { closeDrawer } = useActivityBuilderContext();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const isWebTool = type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL;

  const handleUpdateLayout = useCallback(
    (display: InquisitiveComponentLayout) => {
      const { __typename: _, ...restProperties } = properties;

      updateComponentProperties({
        input: {
          componentId: id,
          sectionId,
          componentData: {
            properties: {
              ...restProperties,
              layout: {
                display,
              },
            },
          },
        },
        properties: {
          ...properties,
          layout: {
            __typename: 'ComponentLayout',
            display,
          },
        },
        typename: isWebTool
          ? 'ActivitiesWebToolComponent'
          : 'ActivitiesLinkComponent',
      });
    },
    [id, isWebTool, sectionId, properties, updateComponentProperties]
  );

  if (!stimulusResourceProperties) return null;

  return (
    <ActivityBuilderDrawerWrapper>
      <ActivityBuilderDrawerHeader
        title={isWebTool ? 'Web Tool' : 'Link'}
        onClose={closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup>
          <SettingsLabel>Layout</SettingsLabel>
          <CheckableSingle
            variant="pill"
            name="link-display"
            selected={properties.layout?.display || COMPONENT_LAYOUT_GRID}
            options={[
              {
                label: 'Row',
                value: COMPONENT_LAYOUT_GRID,
                'aria-label': 'Set layout to row',
                onClick: () => handleUpdateLayout(COMPONENT_LAYOUT_GRID),
              },
              {
                label: 'Card',
                value: COMPONENT_LAYOUT_LIST,
                'aria-label': 'Set layout to Card',
                onClick: () => handleUpdateLayout(COMPONENT_LAYOUT_LIST),
              },
            ]}
          />
        </SettingsGroup>
        <HrStyled $marginBottom={spacing24} />

        <StimulusUsageMessage
          stimulusId={properties.stimulusId || 0}
          componentId={id}
          sectionId={sectionId}
          contentId={lessonId}
        />
        <SettingsTitleView
          title="Link URL"
          value={stimulusResourceProperties.link}
        />
        <SettingsTitleView
          title="Stimulus Name"
          value={stimulusResourceProperties.resourceName}
        />
        <SettingsRichtextView
          title="Stimulus Description"
          value={stimulusResourceProperties.description}
        />
        <SettingsThumbnailView
          title="Stimulus Thumbnail"
          imageUrl={
            stimulusResourceProperties.coverImage?.responsive?.[0].urls.at(-1)
              ?.url
          }
        />
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default LinkEditPreviewDrawer;
